import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragCancelEvent,
  DragOverlay,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import SceneThumbnail from './SceneThumbnail';
import { ThumbnailItemData } from './sceneItemConfig';
import { SceneSizesResult } from './ScenesProvider';

// Re-export for backward compatibility
export type { SceneSizesResult } from './ScenesProvider';

export type DragOverInformation = {
  id: string;
  isAbove: boolean;
  shouldTranslate: { index: number; translate: 'up' | 'down' | 'none' }[];
} | null;

interface ScenesDraggingWrapperProps {
  items: ThumbnailItemData[];
  selectedIds: string[];
  setIsDragging: (isDragging: boolean) => void;
  baseSize: { width: number; height: number };
  activeSize: { width: number; height: number };
  ratioFrame: number;
  calculateRotation: (ratio: number) => number;
  onDragStart: (id: string) => void;
  onDragEnd: (activeId: string, overId: string, moveBackwards: boolean) => void;
  /** When provided, DragOverlay uses per-item sizes (multi-size support). */
  getItemSizes?: (item: ThumbnailItemData) => SceneSizesResult;
  children: (dragOverInformation: DragOverInformation) => React.ReactNode;
}

const ScenesDraggingWrapper: React.FC<ScenesDraggingWrapperProps> = ({
  items,
  selectedIds,
  setIsDragging,
  baseSize,
  activeSize,
  ratioFrame,
  calculateRotation,
  onDragStart,
  onDragEnd,
  getItemSizes,
  children,
}) => {
  const [dragOverInformation, setDragOverInformation] = useState<DragOverInformation>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    const { id } = event.active;
    onDragStart(id as string);
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setIsDragging(false);
    setDragOverInformation(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setDragOverInformation(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !dragOverInformation) return;
    const moveBackwards = active.data.current.sortable.index > over.data.current.sortable.index;
    onDragEnd(active.id as string, over.id as string, moveBackwards);
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (!event.over) return;

    const overId = event.over?.id as string;
    const activeId = event.active.id as string;

    const selectedIndices = items
      .map((item, idx) => selectedIds.includes(item.id) ? idx : -1)
      .filter(idx => idx !== -1);
    if (selectedIndices.length === 0) return;
    const firstSelected = Math.min(...selectedIndices);
    const lastSelected = Math.max(...selectedIndices);
    const overIndex = items.findIndex(item => item.id === overId);
    const moveUp = event.delta.y < 0;

    if (overId === activeId) {
      // Dragging over a selected scene (within the group)
      // Only highlight the group, don't translate others
      let shouldTranslate = [];
      for (let idx = firstSelected; idx <= lastSelected; idx++) {
        shouldTranslate.push({ index: idx, translate: 'none' });
      }
      // Optionally, show a slight up/down for the edge of the group
      if (moveUp && firstSelected > 0) {
        shouldTranslate.push({ index: firstSelected - 1, translate: 'up' });
      } else if (!moveUp && lastSelected < items.length - 1) {
        shouldTranslate.push({ index: lastSelected + 1, translate: 'down' });
      }
      setDragOverInformation({
        id: overId,
        isAbove: moveUp,
        shouldTranslate,
      });
      return;
    }

    // If dragging over a non-selected item
    if (activeId !== overId && !selectedIds.includes(overId)) {
      // If dragging up, the group should move above the overIndex
      // If dragging down, the group should move below the overIndex
      let shouldTranslate = [];
      if (moveUp) {
        // Move group up: shift overIndex and the one above
        shouldTranslate.push({ index: overIndex, translate: 'down' });
        if (overIndex - 1 >= 0 && !selectedIndices.includes(overIndex - 1)) {
          shouldTranslate.push({ index: overIndex - 1, translate: 'up' });
        }
      } else {
        // Move group down: shift overIndex and the one below
        shouldTranslate.push({ index: overIndex, translate: 'up' });
        if (overIndex + 1 < items.length && !selectedIndices.includes(overIndex + 1)) {
          shouldTranslate.push({ index: overIndex + 1, translate: 'down' });
        }
      }
      setDragOverInformation({
        id: overId,
        isAbove: moveUp,
        shouldTranslate,
      });
    }
  };

  // Custom collision detection that requires more substantial overlap
  const customCollisionDetection: CollisionDetection = (args) => {
    const { active, droppableContainers, pointerCoordinates } = args;

    if (!pointerCoordinates || !active.rect.current?.translated) {
      return closestCenter(args);
    }

    const activeRect = active.rect.current.translated;
    const activeHeight = activeRect.height || baseSize.height;
    const activeTop = activeRect.top;
    const activeCenter = activeTop + activeHeight / 2;

    // Filter droppable containers based on center position
    const validCollisions = Array.from(droppableContainers.values())
      .filter((container) => {
        const rect = container.rect.current;
        if (!rect) return false;
        // Only consider this droppable if the active element's center is within its bounds
        return activeCenter >= rect.top && activeCenter <= rect.top + rect.height;
      })
      .map((container) => ({
        id: container.id,
        data: container.data,
      }));

    if (validCollisions.length > 0) {
      return validCollisions;
    }

    // Fallback to closest center if no valid collisions
    return closestCenter(args);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={items.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {children(dragOverInformation)}
      </SortableContext>
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {selectedIds.length > 0 ? (
            <div style={{ position: 'relative' }}>
              {selectedIds.slice(0, 2).map((id, index) => {
                const draggedItem = items.find((i) => i.id === id);
                const draggedSceneSizes = getItemSizes && draggedItem
                  ? getItemSizes(draggedItem)
                  : { baseSize, activeSize, ratio: ratioFrame };
                return (
                  <div
                    style={{
                      transform: `rotate(${index * calculateRotation(draggedSceneSizes.ratio)}deg)`,
                      transformOrigin: 'center',
                      inset: 0,
                      position: 'absolute',
                      zIndex: 10 - index,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      width: `${draggedSceneSizes.baseSize.width}px`,
                      height: `${draggedSceneSizes.baseSize.height}px`,
                    }}
                    key={id}
                  >
                    <SceneThumbnail
                      preview={draggedItem?.preview}
                      selected={true}
                      isActive={true}
                      baseSize={draggedSceneSizes.baseSize}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

export default ScenesDraggingWrapper;
